import React, { useState, useEffect, useMemo } from "react"
import { Row, Col, Card, Table } from "reactstrap"
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table"
import Loader from "pages/Separate/Loader"
import axios from "axios"
import configure from "configure"
import { DatePicker } from "antd"
import NewPagination from "components/Common/Newpagination"

const { RangePicker } = DatePicker

const COLUMNS = [
  {
    Header: "Month",
    accessor: "month",
    className: "text-center",
  },
  {
    Header: "Leaves",
    accessor: "leaves",
    className: "text-center",
  },
]

function TableContainer({
  columns,
  data,
  totalLeaves,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  onFilter,
}) {
  const normalizedData = Array.isArray(data) ? data : []

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
  } = useTable(
    {
      columns,
      data: normalizedData,
      initialState: {
        sortBy: [
          { id: "year", desc: true },
          { id: "month_number", desc: true },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize } = state

  useEffect(() => {
    setPageSize(10)
  }, [setPageSize])

  return (
    <Row>
      <Col xl="12">
        <div className="card-body">
          <Row className="align-items-center justify-content-between mb-3">
            <Col md="auto" className="text-purple fs-5 fw-bold">
              Leave History
            </Col>
            <Col md="auto" className="d-flex align-items-center gap-2">
              <RangePicker
                picker="month"
                value={[fromDate, toDate]}
                onChange={dates => {
                  if (dates) {
                    setFromDate(dates[0])
                    setToDate(dates[1])
                  } else {
                    setFromDate(null)
                    setToDate(null)
                  }
                }}
                format="YYYY-MM"
              />
              <a
                className="btn btn-sm bg-purple text-white"
                style={{ borderRadius: "5px" }}
                onClick={onFilter}
              >
                <i className="bi bi-funnel-fill fs-5"></i>
              </a>
              <div className="text-purple absent-length">{totalLeaves}</div>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table className="table table-bordered" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, index) => (
                      <th
                        key={index}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        className="text-center"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row)
                  return (
                    <tr
                      key={i}
                      {...row.getRowProps()}
                      style={{
                        backgroundColor:
                          row.original.leaves > 4
                            ? "#fff3f3"
                            : row.original.leaves > 2
                            ? "#ffe8bf52"
                            : "transparent",
                      }}
                    >
                      {row.cells.map((cell, index) => (
                        <td
                          key={index}
                          {...cell.getCellProps()}
                          className="text-center"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>

          <Row className="d-flex justify-content-between align-items-center flex-wrap py-3 py-md-0">
            <NewPagination
              gotoPage={gotoPage}
              canPreviousPage={canPreviousPage}
              previousPage={previousPage}
              canNextPage={canNextPage}
              pageIndex={pageIndex}
              pageOptions={pageOptions}
              pageSize={pageSize}
              page={page}
              data={normalizedData}
              nextPage={nextPage}
            />
          </Row>
        </div>
      </Col>
    </Row>
  )
}

const LeaveHistory = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [totalLeaves, setTotalLeaves] = useState(0)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  const user_id = localStorage.getItem("auth_id")

  const fetchData = async (from = null, to = null) => {
    setLoading(true)
    try {
      let response
      if (!from && !to) {
        response = await axios.get(
          `${configure.nodeApi}/pastLeaves/${user_id}`,
          { params: { type: 1 }, timeout: 10000 }
        )
        setTotalLeaves(response.data?.totalLeavesInRange || 0)
      } else {
        const payload = { memberId: user_id, type: 1 }
        if (from) payload.fromDate = from
        if (to) payload.toDate = to
        response = await axios.post(
          `${configure.nodeApi}/adminLeaveCount/${user_id}`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
            timeout: 10000,
          }
        )
        setTotalLeaves(response.data?.totalLeavesInRange || 0)
      }
      const raw = Array.isArray(response.data?.data) ? response.data.data : []
      setData(
        [...raw].sort((a, b) =>
          b.year !== a.year ? b.year - a.year : b.month_number - a.month_number
        ).reverse()
      )
    } catch (error) {
      console.error(
        "LeaveHistory fetch error:",
        error.response?.data || error.message
      )
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const columns = useMemo(() => COLUMNS, [])

  return (
    <div>
      {isLoading && <Loader />}
      <div className="position-relative">
        <Row>
          <Col lg="12">
            <Card>
              <div className="responsive-table">
                <TableContainer
                  columns={columns}
                  data={data}
                  totalLeaves={totalLeaves}
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                  onFilter={() => fetchData(fromDate, toDate)}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default LeaveHistory
